import { z } from 'zod';
import { sanitizePlayerName, sanitizeHostName, sanitizeAvatar, sanitizeReason, normalizeRoomCode, isValidRoomCode, MAX_AVATAR_LENGTH, } from './sanitize.js';
import { GamePhaseSchema, ConnectionStatusSchema, PlayerRoleSchema } from './schemas.js';
// =============================================================================
// Base Message Envelope
// =============================================================================
export const MessageEnvelope = z.object({
    id: z.string().uuid(),
    type: z.string(),
    timestamp: z.number(),
    correlationId: z.string().uuid().optional(),
});
// =============================================================================
// Connection Messages
// =============================================================================
/** Host creates a room */
export const CreateRoom = z.object({
    type: z.literal('CREATE_ROOM'),
    hostName: z
        .string()
        .min(1, 'Host name is required')
        .max(32, 'Host name too long')
        .transform((name) => sanitizeHostName(name))
        .refine((name) => name.length >= 1, 'Host name cannot be empty after sanitization'),
    gameType: z.string().optional(),
    maxPlayers: z.number().int().min(2).max(100).default(8),
});
/** Room created response */
export const RoomCreated = z.object({
    type: z.literal('ROOM_CREATED'),
    roomId: z.string(),
    roomCode: z.string().length(6),
    expiresAt: z.number(),
});
/** Player joins a room */
export const JoinRoom = z.object({
    type: z.literal('JOIN_ROOM'),
    roomCode: z
        .string()
        .min(6, 'Room code must be 6 characters')
        .max(6, 'Room code must be 6 characters')
        .transform((code) => normalizeRoomCode(code))
        .refine(isValidRoomCode, 'Invalid room code format'),
    playerName: z
        .string()
        .min(1, 'Player name is required')
        .max(24, 'Player name too long') // Accept up to 24 from client, sanitize to 12
        .transform((name) => sanitizePlayerName(name))
        .refine((name) => name.length >= 1, 'Player name cannot be empty after sanitization'),
    avatar: z
        .string()
        .max(MAX_AVATAR_LENGTH, 'Avatar too long')
        .transform((avatar) => sanitizeAvatar(avatar))
        .optional(),
});
/** Relayed join request from gateway to host */
export const RelayedJoinRequest = z.object({
    type: z.literal('RELAYED_JOIN_REQUEST'),
    requestId: z.string(),
    playerName: z.string(),
    avatar: z.string().optional(),
    timestamp: z.number(),
});
/** Relayed full state request from gateway to host */
export const RelayedFullStateRequest = z.object({
    type: z.literal('RELAYED_FULL_STATE_REQUEST'),
    playerId: z.string(),
    timestamp: z.number(),
});
/** Relayed game catalog request from gateway to host */
export const RelayedGameCatalogRequest = z.object({
    type: z.literal('RELAYED_GAME_CATALOG_REQUEST'),
    timestamp: z.number(),
});
/** Host decision for a join request */
export const HostJoinDecision = z.object({
    type: z.literal('HOST_JOIN_DECISION'),
    requestId: z.string(),
    success: z.boolean(),
    playerId: z.string().optional(),
    playerName: z.string().optional(),
    avatar: z.string().optional(),
    role: PlayerRoleSchema.optional(),
    isOwner: z.boolean().optional(),
    roomPhase: GamePhaseSchema.optional(),
    gameType: z.string().optional(),
    error: z.enum(['ROOM_CLOSED', 'NAME_TAKEN', 'INVALID_NAME']).optional(),
    timestamp: z.number(),
});
/** Join result */
export const JoinResult = z.object({
    type: z.literal('JOIN_RESULT'),
    success: z.boolean(),
    playerId: z.string().optional(),
    sessionToken: z.string().optional(),
    isOwner: z.boolean().optional(), // True if this player is the lobby owner (picks the game)
    isSpectator: z.boolean().optional(), // True if room was full and player is spectating
    isObserver: z.boolean().optional(), // True if joined during active game (will become player on return to lobby)
    roomInfo: z
        .object({
        roomId: z.string(),
        roomCode: z.string(),
        hostName: z.string(),
        gameType: z.string().optional(),
        phase: GamePhaseSchema,
        players: z.array(z.object({
            id: z.string(),
            name: z.string(),
            avatar: z.string().optional(),
            connectionStatus: ConnectionStatusSchema,
            score: z.number(),
        })),
        spectators: z
            .array(z.object({
            id: z.string(),
            name: z.string(),
            avatar: z.string().optional(),
            connectionStatus: ConnectionStatusSchema,
            score: z.number(),
        }))
            .optional(),
        observers: z
            .array(z.object({
            id: z.string(),
            name: z.string(),
            avatar: z.string().optional(),
            connectionStatus: ConnectionStatusSchema,
            score: z.number(),
        }))
            .optional(),
        owner: z
            .object({
            id: z.string(),
            name: z.string(),
        })
            .optional(),
        maxPlayers: z.number(),
        createdAt: z.number(),
    })
        .optional(),
    error: z
        .enum(['INVALID_CODE', 'ROOM_FULL', 'ROOM_CLOSED', 'NAME_TAKEN', 'INVALID_NAME'])
        .optional(),
    reason: z.string().optional(), // Human-readable reason for error (e.g., "Name contains inappropriate content")
});
/** Player joined notification (sent to all clients) */
export const PlayerJoined = z.object({
    type: z.literal('PLAYER_JOINED'),
    playerId: z.string(),
    playerName: z.string(),
    avatar: z.string().optional(),
});
/** Player left notification */
export const PlayerLeft = z.object({
    type: z.literal('PLAYER_LEFT'),
    playerId: z.string(),
    reason: z.enum(['disconnect', 'kicked', 'quit']),
});
/** Player disconnected notification (no removal yet) */
export const PlayerDisconnected = z.object({
    type: z.literal('PLAYER_DISCONNECTED'),
    playerId: z.string(),
});
/** Player reconnected notification (sent when a player successfully reconnects) */
export const PlayerReconnected = z.object({
    type: z.literal('PLAYER_RECONNECTED'),
    playerId: z.string(),
});
/** Leave room request (player explicitly leaves) */
export const LeaveRoom = z.object({
    type: z.literal('LEAVE_ROOM'),
});
/** Host removes a player for any reason */
export const HostRemovePlayer = z.object({
    type: z.literal('HOST_REMOVE_PLAYER'),
    playerId: z.string(),
    reason: z.enum(['disconnect', 'kicked', 'quit']),
});
/** Host instructs a role change for an existing player */
export const HostPlayerRoleUpdate = z.object({
    type: z.literal('HOST_PLAYER_ROLE_UPDATE'),
    playerId: z.string(),
    role: PlayerRoleSchema,
    message: z.string().optional(),
    reason: z.string().optional(),
});
/** Owner changed notification (when party leader leaves) */
export const OwnerChanged = z.object({
    type: z.literal('OWNER_CHANGED'),
    newOwnerId: z.string(),
    newOwnerName: z.string(),
});
/** Host requests to change the room owner/party leader */
export const ChangeOwner = z.object({
    type: z.literal('CHANGE_OWNER'),
    newOwnerId: z.string(),
});
/** Player updates their avatar */
export const UpdateAvatar = z.object({
    type: z.literal('UPDATE_AVATAR'),
    avatar: z
        .string()
        .max(MAX_AVATAR_LENGTH, 'Avatar too long')
        .transform((avatar) => sanitizeAvatar(avatar)),
});
/** Avatar changed notification (sent to all clients) */
export const AvatarChanged = z.object({
    type: z.literal('AVATAR_CHANGED'),
    playerId: z.string(),
    avatar: z.string(),
});
/** Spectator promoted to player notification */
export const PromotedToPlayer = z.object({
    type: z.literal('PROMOTED_TO_PLAYER'),
    message: z.string(),
});
/** Player demoted to spectator notification (e.g., game has fewer max players than room) */
export const DemotedToSpectator = z.object({
    type: z.literal('DEMOTED_TO_SPECTATOR'),
    message: z.string(),
    reason: z.enum(['game_max_players', 'manual']),
});
/** Observer joined notification (late-joiner during active game) */
export const ObserverJoined = z.object({
    type: z.literal('OBSERVER_JOINED'),
    playerId: z.string(),
    playerName: z.string(),
    avatar: z.string().optional(),
});
/** Observer left notification */
export const ObserverLeft = z.object({
    type: z.literal('OBSERVER_LEFT'),
    playerId: z.string(),
    reason: z.enum(['disconnect', 'kicked', 'quit']),
});
/** Session replaced notification (another tab/device connected) */
export const SessionReplaced = z.object({
    type: z.literal('SESSION_REPLACED'),
    message: z.string(),
});
/** Host replaced notification (another host screen connected to this lobby) */
export const HostReplaced = z.object({
    type: z.literal('HOST_REPLACED'),
    message: z.string(),
});
/** Reconnection attempt */
export const Reconnect = z.object({
    type: z.literal('RECONNECT'),
    sessionToken: z.string(),
});
/** Reconnection result */
export const ReconnectResult = z.object({
    type: z.literal('RECONNECT_RESULT'),
    success: z.boolean(),
    playerId: z.string().optional(),
    isOwner: z.boolean().optional(),
    isSpectator: z.boolean().optional(),
    isObserver: z.boolean().optional(),
    roomInfo: z
        .object({
        roomId: z.string(),
        roomCode: z.string(),
        hostName: z.string(),
        gameType: z.string().optional(),
        phase: GamePhaseSchema,
        players: z.array(z.object({
            id: z.string(),
            name: z.string(),
            avatar: z.string().optional(),
            connectionStatus: ConnectionStatusSchema,
            score: z.number(),
        })),
        spectators: z
            .array(z.object({
            id: z.string(),
            name: z.string(),
            avatar: z.string().optional(),
            connectionStatus: ConnectionStatusSchema,
            score: z.number(),
        }))
            .optional(),
        observers: z
            .array(z.object({
            id: z.string(),
            name: z.string(),
            avatar: z.string().optional(),
            connectionStatus: ConnectionStatusSchema,
            score: z.number(),
        }))
            .optional(),
        owner: z
            .object({
            id: z.string(),
            name: z.string(),
        })
            .optional(),
        maxPlayers: z.number(),
        createdAt: z.number(),
    })
        .optional(),
    gameState: z.unknown().optional(),
    gamePhase: z.string().optional(),
    gameStateVersion: z.number().optional(),
    gameConfig: z.unknown().optional(),
});
/** Host reconnection attempt */
export const ReconnectHost = z.object({
    type: z.literal('RECONNECT_HOST'),
    roomId: z.string(),
});
/** Host reconnection result */
export const ReconnectHostResult = z.object({
    type: z.literal('RECONNECT_HOST_RESULT'),
    success: z.boolean(),
    roomInfo: z
        .object({
        roomId: z.string(),
        roomCode: z.string(),
        phase: GamePhaseSchema,
        gameType: z.string().optional(),
        players: z.array(z.object({
            id: z.string(),
            name: z.string(),
            avatar: z.string().optional(),
            connectionStatus: ConnectionStatusSchema,
            score: z.number(),
            isOwner: z.boolean().optional(),
        })),
        spectators: z
            .array(z.object({
            id: z.string(),
            name: z.string(),
            avatar: z.string().optional(),
            connectionStatus: ConnectionStatusSchema,
            score: z.number(),
        }))
            .optional(),
        owner: z
            .object({
            id: z.string(),
            name: z.string(),
        })
            .optional(),
        maxPlayers: z.number(),
        createdAt: z.number(),
    })
        .optional(),
    gameState: z.unknown().optional(),
    gameStateVersion: z.number().optional(),
    gameConfig: z.unknown().optional(),
});
/** Host restores a room from persisted state (after gateway restart) */
export const RestoreRoom = z.object({
    type: z.literal('RESTORE_ROOM'),
    roomId: z.string(),
    roomCode: z.string(),
    phase: GamePhaseSchema,
    players: z.array(z.object({
        id: z.string(),
        name: z.string(),
        avatar: z.string().optional(),
        score: z.number(),
        isOwner: z.boolean().optional(),
    })),
    spectators: z
        .array(z.object({
        id: z.string(),
        name: z.string(),
        avatar: z.string().optional(),
        score: z.number(),
    }))
        .optional(),
    owner: z
        .object({
        id: z.string(),
        name: z.string(),
    })
        .optional(),
    gameType: z.string().optional(),
    gameState: z.unknown().optional(),
    maxPlayers: z.number().default(8),
});
/** Restore room result */
export const RestoreRoomResult = z.object({
    type: z.literal('RESTORE_ROOM_RESULT'),
    success: z.boolean(),
    roomId: z.string().optional(),
    roomCode: z.string().optional(),
    error: z.string().optional(),
});
// =============================================================================
// Game Lifecycle Messages
// =============================================================================
/** Lobby owner selects a game */
export const SelectGame = z.object({
    type: z.literal('SELECT_GAME'),
    gameType: z.string(),
});
/** Relayed select game request from gateway to host */
export const RelayedSelectGame = z.object({
    type: z.literal('RELAYED_SELECT_GAME'),
    playerId: z.string(),
    gameType: z.string(),
    timestamp: z.number(),
});
/** Game selected notification (broadcast) */
export const GameSelected = z.object({
    type: z.literal('GAME_SELECTED'),
    gameType: z.string(),
    gameName: z.string(),
    minPlayers: z.number(),
    maxPlayers: z.number(),
});
/** Available game catalog entry */
export const GameCatalogEntry = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    minPlayers: z.number(),
    maxPlayers: z.number(),
    displayColor: z.string(),
    icon: z.string(),
});
/** Host-authored game catalog */
export const HostGameCatalog = z.object({
    type: z.literal('HOST_GAME_CATALOG'),
    games: z.array(GameCatalogEntry),
});
/** Relayed game catalog for controllers */
export const GameCatalog = z.object({
    type: z.literal('GAME_CATALOG'),
    games: z.array(GameCatalogEntry),
});
/** Host-selected game (authoritative) */
export const HostGameSelected = z.object({
    type: z.literal('HOST_GAME_SELECTED'),
    gameType: z.string(),
    gameName: z.string(),
    minPlayers: z.number(),
    maxPlayers: z.number(),
    displayColor: z.string().optional(),
    icon: z.string().optional(),
});
/** Host starts the game */
export const StartGame = z.object({
    type: z.literal('START_GAME'),
    gameType: z.string(),
    config: z.record(z.unknown()).optional(),
});
/** Relayed start game request from gateway to host */
export const RelayedStartGame = z.object({
    type: z.literal('RELAYED_START_GAME'),
    playerId: z.string(),
    gameType: z.string(),
    config: z.record(z.unknown()).optional(),
    timestamp: z.number(),
});
/** Game started notification */
export const GameStarted = z.object({
    type: z.literal('GAME_STARTED'),
    gameType: z.string(),
    config: z.record(z.unknown()),
    initialState: z.unknown().optional(),
    usedResources: z
        .object({
        triviaQuestionIds: z.array(z.string()).optional(),
        promptPartyPromptIds: z.array(z.string()).optional(),
        promptPartyAnswerIds: z.array(z.string()).optional(),
        hotTakesPromptIds: z.array(z.string()).optional(),
        tallTalesQuestionIds: z.array(z.string()).optional(),
    })
        .optional(),
});
/** Host-authored game start */
export const HostGameStarted = z.object({
    type: z.literal('HOST_GAME_STARTED'),
    gameType: z.string(),
    config: z.record(z.unknown()),
    startedAt: z.number().optional(),
    usedResources: z
        .object({
        triviaQuestionIds: z.array(z.string()).optional(),
        promptPartyPromptIds: z.array(z.string()).optional(),
        promptPartyAnswerIds: z.array(z.string()).optional(),
        hotTakesPromptIds: z.array(z.string()).optional(),
        tallTalesQuestionIds: z.array(z.string()).optional(),
    })
        .optional(),
});
/** Player input/action */
export const PlayerInput = z.object({
    type: z.literal('PLAYER_INPUT'),
    action: z.string(),
    data: z.unknown(),
    sequence: z.number(),
    // Optional: When host forwards an action on behalf of a player (for gateway-handled actions)
    playerId: z.string().optional(),
});
// =============================================================================
// Host-Side Game Engine Messages
// =============================================================================
/** Relayed action from gateway to host (when host runs game engine) */
export const RelayedAction = z.object({
    type: z.literal('RELAYED_ACTION'),
    playerId: z.string(),
    action: z.string(),
    data: z.unknown(),
    sequence: z.number(),
    timestamp: z.number(),
});
/** Relayed lobby action from gateway to host */
export const RelayedLobbyAction = z.object({
    type: z.literal('RELAYED_LOBBY_ACTION'),
    playerId: z.string(),
    action: z.enum(['RETURN_TO_LOBBY', 'LEAVE_ROOM']),
});
/** Host sends computed state to gateway for distribution to controllers */
export const HostStateUpdate = z.object({
    type: z.literal('HOST_STATE_UPDATE'),
    version: z.number(),
    /** Public state (sent to host display) */
    publicState: z.unknown(),
    /** Private states per player (sent individually to each controller) */
    privateStates: z.record(z.string(), z.unknown()),
    /** Game phase for routing */
    phase: z.string(),
    /** Timestamp for ordering */
    timestamp: z.number(),
    /** Full game state for host reconnects (optional) */
    fullState: z.unknown().optional(),
    /** Game config snapshot for host reconnects (optional) */
    config: z.unknown().optional(),
});
/** Host reports RTC connection status for a player */
export const HostRtcStatus = z.object({
    type: z.literal('HOST_RTC_STATUS'),
    playerId: z.string(),
    rtcState: z.enum(['new', 'connecting', 'connected', 'disconnected', 'failed', 'closed']),
    timestamp: z.number(),
});
/** Host notifies gateway that game ended */
export const HostGameEnded = z.object({
    type: z.literal('HOST_GAME_ENDED'),
    gameType: z.string().optional(),
    winners: z.array(z.string()),
    finalScores: z.record(z.string(), z.number()),
    reason: z.string(),
    startedAt: z.number().optional(),
    endedAt: z.number().optional(),
    durationMs: z.number().optional(),
    timestamp: z.number(),
});
/** Host-authored lobby snapshot broadcast through the gateway */
export const HostLobbyUpdate = z.object({
    type: z.literal('HOST_LOBBY_UPDATE'),
    phase: GamePhaseSchema,
    gameType: z.string().optional(),
    roomId: z.string().optional(),
    roomCode: z.string().optional(),
    players: z.array(z.object({
        id: z.string(),
        name: z.string(),
        avatar: z.string().optional(),
        connectionStatus: ConnectionStatusSchema,
        score: z.number(),
        isOwner: z.boolean().optional(),
    })),
    spectators: z
        .array(z.object({
        id: z.string(),
        name: z.string(),
        avatar: z.string().optional(),
        connectionStatus: ConnectionStatusSchema,
        score: z.number(),
        isOwner: z.boolean().optional(),
    }))
        .optional(),
    observers: z
        .array(z.object({
        id: z.string(),
        name: z.string(),
        avatar: z.string().optional(),
        connectionStatus: ConnectionStatusSchema,
        score: z.number(),
        isOwner: z.boolean().optional(),
    }))
        .optional(),
    owner: z
        .object({
        id: z.string(),
        name: z.string(),
    })
        .optional(),
    timestamp: z.number(),
});
/** Host-authored game state envelope that can be forwarded directly to controllers */
export const HostGameState = z.object({
    type: z.literal('HOST_GAME_STATE'),
    version: z.number(),
    phase: z.string(),
    timestamp: z.number(),
    /** Public state (used for host display and spectators) */
    publicState: z.unknown().optional(),
    /** Private state keyed by player for gateway fan-out */
    privateStates: z.record(z.string(), z.unknown()).optional(),
    /** Recipient-focused state payload (used on the way out to clients) */
    state: z.unknown().optional(),
    fullState: z.unknown().optional(),
    config: z.unknown().optional(),
    previousVersion: z.number().optional(),
});
/** Host acknowledges an action was processed */
export const ActionAck = z.object({
    type: z.literal('ACTION_ACK'),
    playerId: z.string(),
    sequence: z.number(),
    accepted: z.boolean(),
    error: z.string().optional(),
});
/** Global platform stats broadcast to all hosts */
export const GlobalStats = z.object({
    type: z.literal('GLOBAL_STATS'),
    players: z.number(),
    games: z.number(),
});
/** State update (sent to all clients) */
export const StateUpdate = z.object({
    type: z.literal('STATE_UPDATE'),
    version: z.number(),
    previousVersion: z.number().optional(), // Used to detect missed updates
    updateType: z.enum(['full', 'delta']),
    publicState: z.unknown(),
    /** Checksum of publicState for verification (computed every N updates) */
    checksum: z.string().optional(),
    timestamp: z.number(),
});
/** Request full state resync (sent when missed updates detected) */
export const RequestFullState = z.object({
    type: z.literal('REQUEST_FULL_STATE'),
});
/** Full state sync response (sent after REQUEST_FULL_STATE) */
export const FullStateSync = z.object({
    type: z.literal('FULL_STATE_SYNC'),
    /** Current version number */
    version: z.number().int().nonnegative(),
    /** Checksum for verification */
    checksum: z.string(),
    /** Full public game state */
    publicState: z.unknown(),
    /** Private state for this player (if applicable) */
    privateState: z.unknown().optional(),
    /** Server timestamp */
    timestamp: z.number(),
});
/** Private state (sent to individual player) */
export const PrivateState = z.object({
    type: z.literal('PRIVATE_STATE'),
    state: z.unknown(),
});
/** Game ended */
export const GameEnded = z.object({
    type: z.literal('GAME_ENDED'),
    winners: z.array(z.string()),
    finalScores: z.record(z.number()),
    reason: z.enum(['completed', 'aborted', 'timeout']),
});
/** Game error - sent when an unexpected error occurs */
export const GameError = z.object({
    type: z.literal('GAME_ERROR'),
    message: z.string(),
});
// =============================================================================
// Administrative Messages
// =============================================================================
/** Kick a player (host only) */
export const KickPlayer = z.object({
    type: z.literal('KICK_PLAYER'),
    playerId: z.string(),
    reason: z
        .string()
        .max(200, 'Reason too long')
        .transform((reason) => sanitizeReason(reason))
        .optional(),
});
/** Close the room (host only) */
export const CloseRoom = z.object({
    type: z.literal('CLOSE_ROOM'),
    reason: z
        .string()
        .max(200, 'Reason too long')
        .transform((reason) => sanitizeReason(reason))
        .optional(),
});
/** Return to lobby after game ends (host only) */
export const ReturnToLobby = z.object({
    type: z.literal('RETURN_TO_LOBBY'),
});
/** Relayed return-to-lobby request from gateway to host */
export const RelayedReturnToLobby = z.object({
    type: z.literal('RELAYED_RETURN_TO_LOBBY'),
    playerId: z.string(),
    timestamp: z.number(),
});
/** Pause the game (host only) */
export const PauseGame = z.object({
    type: z.literal('PAUSE_GAME'),
});
/** Relayed pause game request from gateway to host */
export const RelayedPauseGame = z.object({
    type: z.literal('RELAYED_PAUSE_GAME'),
    playerId: z.string(),
    timerState: z.unknown().optional(),
    timestamp: z.number(),
});
/** Resume the game (host only) */
export const ResumeGame = z.object({
    type: z.literal('RESUME_GAME'),
});
/** Relayed resume game request from gateway to host */
export const RelayedResumeGame = z.object({
    type: z.literal('RELAYED_RESUME_GAME'),
    playerId: z.string(),
    timerState: z.unknown().optional(),
    timestamp: z.number(),
});
/** Game paused notification */
export const GamePaused = z.object({
    type: z.literal('GAME_PAUSED'),
    pausedAt: z.number(),
    pausedBy: z.string().optional(), // 'host' or playerId if we allow players to pause
});
/** Host-authored pause */
export const HostGamePaused = z.object({
    type: z.literal('HOST_GAME_PAUSED'),
    pausedAt: z.number(),
    pausedBy: z.string().optional(),
});
/** Game resumed notification */
export const GameResumed = z.object({
    type: z.literal('GAME_RESUMED'),
    resumedAt: z.number(),
});
/** Host-authored resume */
export const HostGameResumed = z.object({
    type: z.literal('HOST_GAME_RESUMED'),
    resumedAt: z.number(),
});
/** Returned to lobby notification */
export const ReturnedToLobby = z.object({
    type: z.literal('RETURNED_TO_LOBBY'),
});
/** Host-authored return to lobby */
export const HostReturnedToLobby = z.object({
    type: z.literal('HOST_RETURNED_TO_LOBBY'),
});
/** Room closed notification (sent to all players when room is closed) */
export const RoomClosed = z.object({
    type: z.literal('ROOM_CLOSED'),
    reason: z.string(),
});
/** Server restarting notification (sent before graceful shutdown) */
export const ServerRestarting = z.object({
    type: z.literal('SERVER_RESTARTING'),
    /** Milliseconds until server will restart */
    restartIn: z.number(),
    /** Optional message for users */
    message: z.string().optional(),
});
// =============================================================================
// Message Acknowledgment
// =============================================================================
/** Acknowledgment for reliable message delivery */
export const MessageAck = z.object({
    type: z.literal('MESSAGE_ACK'),
    /** ID of the message being acknowledged */
    messageId: z.string(),
    /** Timestamp when server received the message */
    receivedAt: z.number(),
});
/** Batch acknowledgment for multiple messages */
export const BatchAck = z.object({
    type: z.literal('BATCH_ACK'),
    /** IDs of messages being acknowledged */
    messageIds: z.array(z.string()),
    /** Highest sequence number received */
    highestSequence: z.number().int().nonnegative(),
    /** Timestamp when server processed the batch */
    receivedAt: z.number(),
});
/** Error message */
export const ErrorMessage = z.object({
    type: z.literal('ERROR'),
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
    retryAfter: z.number().optional(), // Milliseconds until retry allowed (for rate limiting)
});
/** Standard error codes */
export const ErrorCodes = {
    // Rate limiting
    RATE_LIMITED: 'RATE_LIMITED',
    TOO_MANY_FAILED_ATTEMPTS: 'TOO_MANY_FAILED_ATTEMPTS',
    MESSAGE_TOO_LARGE: 'MESSAGE_TOO_LARGE',
    // Connection
    PARSE_ERROR: 'PARSE_ERROR',
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    NOT_IN_ROOM: 'NOT_IN_ROOM',
    ROOM_NOT_FOUND: 'ROOM_NOT_FOUND',
    // Authorization
    NOT_HOST: 'NOT_HOST',
    NOT_PLAYER: 'NOT_PLAYER',
    NOT_OWNER: 'NOT_OWNER',
    NOT_AUTHORIZED: 'NOT_AUTHORIZED',
    // Game
    INVALID_GAME: 'INVALID_GAME',
    NOT_PLAYING: 'NOT_PLAYING',
    START_FAILED: 'START_FAILED',
    RETURN_FAILED: 'RETURN_FAILED',
    NOT_ENOUGH_PLAYERS: 'NOT_ENOUGH_PLAYERS',
    // Room
    CREATE_ROOM_FAILED: 'CREATE_ROOM_FAILED',
    PLAYER_NOT_FOUND: 'PLAYER_NOT_FOUND',
};
// =============================================================================
// Heartbeat
// =============================================================================
export const Ping = z.object({
    type: z.literal('PING'),
    sentAt: z.number(),
});
export const Pong = z.object({
    type: z.literal('PONG'),
    sentAt: z.number(),
    receivedAt: z.number(),
});
/** Latency pong - response to latency ping from gateway */
export const LatencyPong = z.object({
    type: z.literal('LATENCY_PONG'),
});
// =============================================================================
// Client Logging (forwarded to server for debugging)
// =============================================================================
export const ClientLog = z.object({
    type: z.literal('CLIENT_LOG'),
    level: z.enum(['trace', 'debug', 'info', 'warn', 'error']),
    message: z.string(),
    data: z.unknown().optional(),
    timestamp: z.number(),
});
// =============================================================================
// WebRTC Signaling Messages
// =============================================================================
/** WebRTC SDP offer - sent by initiator (typically host) */
export const RtcOffer = z.object({
    type: z.literal('RTC_OFFER'),
    targetId: z.string(), // PlayerId of the target peer
    sdp: z.string(), // SDP offer string
    restart: z.boolean().optional(), // ICE restart offer
});
/** WebRTC SDP answer - sent by responder */
export const RtcAnswer = z.object({
    type: z.literal('RTC_ANSWER'),
    targetId: z.string(), // PlayerId of the target peer
    sdp: z.string(), // SDP answer string
});
/** WebRTC ICE candidate - sent during connection negotiation */
export const RtcIceCandidate = z.object({
    type: z.literal('RTC_ICE_CANDIDATE'),
    targetId: z.string(), // PlayerId of the target peer
    candidate: z.string(), // ICE candidate string
    sdpMid: z.string().nullable(),
    sdpMLineIndex: z.number().nullable(),
});
/** WebRTC offer relayed from another peer (via gateway) */
export const RtcOfferRelay = z.object({
    type: z.literal('RTC_OFFER_RELAY'),
    fromId: z.string(), // PlayerId of sender (host or player)
    sdp: z.string(),
    restart: z.boolean().optional(), // ICE restart offer
});
/** WebRTC answer relayed from another peer (via gateway) */
export const RtcAnswerRelay = z.object({
    type: z.literal('RTC_ANSWER_RELAY'),
    fromId: z.string(),
    sdp: z.string(),
});
/** WebRTC ICE candidate relayed from another peer (via gateway) */
export const RtcIceCandidateRelay = z.object({
    type: z.literal('RTC_ICE_CANDIDATE_RELAY'),
    fromId: z.string(),
    candidate: z.string(),
    sdpMid: z.string().nullable(),
    sdpMLineIndex: z.number().nullable(),
});
/** Request to initiate WebRTC connection (controller -> gateway -> host) */
export const RtcConnect = z.object({
    type: z.literal('RTC_CONNECT'),
});
// =============================================================================
// Host Status Messages
// =============================================================================
/** Host connection status broadcast to players */
export const HostStatus = z.object({
    type: z.literal('HOST_STATUS'),
    connected: z.boolean(),
    message: z.string().optional(),
});
/** WebRTC connection state update */
export const RtcConnectionState = z.object({
    type: z.literal('RTC_CONNECTION_STATE'),
    peerId: z.string(),
    state: z.enum(['connecting', 'connected', 'disconnected', 'failed']),
});
// =============================================================================
// Player Reactions
// =============================================================================
/** Available reaction types */
export const ReactionType = z.enum([
    // Basic reactions
    'thumbsUp',
    'thumbsDown',
    'laugh',
    'love',
    'wow',
    'sad',
    'angry',
    'clap',
    // Extended reactions
    'thinking',
    'eyeroll',
    'fire',
    'skull',
    'eyes',
    'hundred',
    'party',
    'shocked',
]);
/** Player sends a reaction (client -> server) */
export const PlayerReaction = z.object({
    type: z.literal('PLAYER_REACTION'),
    reaction: ReactionType,
});
/** Reaction broadcast (server -> all clients) */
export const ReactionBroadcast = z.object({
    type: z.literal('REACTION_BROADCAST'),
    playerId: z.string(),
    playerName: z.string(),
    reaction: ReactionType,
    timestamp: z.number(),
});
// =============================================================================
// Synchronized Effects
// =============================================================================
/** Effect types that can be triggered across devices */
export const EffectType = z.enum([
    'confetti',
    'screenFlash',
    'shake',
    'celebrate',
    'alert',
    'countdown',
    'vibrate',
]);
/** Effect data for customization */
export const EffectData = z.object({
    color: z.string().optional(),
    intensity: z.number().min(0).max(1).optional(),
    duration: z.number().optional(), // ms
    message: z.string().optional(),
    count: z.number().optional(), // For countdown
});
/** Synchronized effect message (server -> clients) */
export const Effect = z.object({
    type: z.literal('EFFECT'),
    effect: EffectType,
    target: z.union([
        z.literal('all'),
        z.literal('host'),
        z.literal('controllers'),
        z.string(), // PlayerId
        z.array(z.string()), // PlayerId[]
    ]),
    data: EffectData.optional(),
    timestamp: z.number(), // For synchronization
});
// =============================================================================
// Warmup Games (played in lobby while waiting for players)
// =============================================================================
/** Warmup game types */
export const WarmupGameType = z.enum(['doodle', 'poll', 'reaction']);
/** Start a warmup game (auto or host-triggered) */
export const WarmupStart = z.object({
    type: z.literal('WARMUP_START'),
    gameType: WarmupGameType,
});
/** Relayed warmup start from gateway to host */
export const RelayedWarmupStart = z.object({
    type: z.literal('RELAYED_WARMUP_START'),
    playerId: z.string(),
    gameType: WarmupGameType,
    timestamp: z.number(),
});
/** Warmup game started notification */
export const WarmupStarted = z.object({
    type: z.literal('WARMUP_STARTED'),
    gameType: WarmupGameType,
    state: z.unknown(),
});
/** Host-authoritative warmup start */
export const HostWarmupStarted = z.object({
    type: z.literal('HOST_WARMUP_STARTED'),
    gameType: WarmupGameType,
    state: z.unknown(),
});
/** Player input for warmup game */
export const WarmupInput = z.object({
    type: z.literal('WARMUP_INPUT'),
    action: z.string(),
    data: z.unknown(),
});
/** Relayed warmup input from gateway to host */
export const RelayedWarmupInput = z.object({
    type: z.literal('RELAYED_WARMUP_INPUT'),
    playerId: z.string(),
    action: z.string(),
    data: z.unknown(),
    timestamp: z.number(),
});
/** Warmup game state update */
export const WarmupStateUpdate = z.object({
    type: z.literal('WARMUP_STATE_UPDATE'),
    state: z.unknown(),
    timestamp: z.number(),
});
/** Host-authoritative warmup state update */
export const HostWarmupState = z.object({
    type: z.literal('HOST_WARMUP_STATE'),
    state: z.unknown(),
    timestamp: z.number(),
});
/** Stop warmup game (when main game starts) */
export const WarmupStop = z.object({
    type: z.literal('WARMUP_STOP'),
});
/** Relayed warmup stop from gateway to host */
export const RelayedWarmupStop = z.object({
    type: z.literal('RELAYED_WARMUP_STOP'),
    playerId: z.string(),
    timestamp: z.number(),
});
/** Warmup game stopped notification */
export const WarmupStopped = z.object({
    type: z.literal('WARMUP_STOPPED'),
    reason: z.enum(['game_starting', 'host_stopped', 'room_closed']),
});
/** Host-authoritative warmup stop */
export const HostWarmupStopped = z.object({
    type: z.literal('HOST_WARMUP_STOPPED'),
    reason: z.enum(['game_starting', 'host_stopped', 'room_closed']),
});
// =============================================================================
// Union of All Messages
// =============================================================================
export const ClientMessage = z.discriminatedUnion('type', [
    CreateRoom,
    JoinRoom,
    HostJoinDecision,
    LeaveRoom,
    Reconnect,
    ReconnectHost,
    RestoreRoom,
    SelectGame,
    StartGame,
    PlayerInput,
    RequestFullState,
    KickPlayer,
    HostRemovePlayer,
    HostPlayerRoleUpdate,
    ChangeOwner,
    CloseRoom,
    ReturnToLobby,
    PauseGame,
    ResumeGame,
    UpdateAvatar,
    Ping,
    LatencyPong,
    ClientLog,
    // Player reactions
    PlayerReaction,
    // WebRTC signaling
    RtcConnect,
    RtcOffer,
    RtcAnswer,
    RtcIceCandidate,
    // Warmup games
    WarmupStart,
    WarmupInput,
    WarmupStop,
    HostWarmupStarted,
    HostWarmupState,
    HostWarmupStopped,
    // Host-side game engine
    HostStateUpdate,
    HostGameState,
    HostLobbyUpdate,
    HostRtcStatus,
    HostGameEnded,
    HostGameCatalog,
    HostGameSelected,
    HostGameStarted,
    HostGamePaused,
    HostGameResumed,
    HostReturnedToLobby,
    ActionAck,
]);
export const ServerMessage = z.discriminatedUnion('type', [
    RoomCreated,
    RelayedJoinRequest,
    RelayedFullStateRequest,
    JoinResult,
    PlayerJoined,
    PlayerLeft,
    PlayerDisconnected,
    PlayerReconnected,
    OwnerChanged,
    AvatarChanged,
    PromotedToPlayer,
    DemotedToSpectator,
    ObserverJoined,
    ObserverLeft,
    SessionReplaced,
    HostReplaced,
    ReconnectResult,
    ReconnectHostResult,
    RestoreRoomResult,
    GameSelected,
    GameStarted,
    StateUpdate,
    FullStateSync,
    PrivateState,
    GameEnded,
    GameError,
    GamePaused,
    GameResumed,
    ReturnedToLobby,
    RoomClosed,
    ServerRestarting,
    MessageAck,
    BatchAck,
    ErrorMessage,
    Pong,
    HostStatus,
    GameCatalog,
    // Player reactions
    ReactionBroadcast,
    // WebRTC signaling relays
    RtcOfferRelay,
    RtcAnswerRelay,
    RtcIceCandidateRelay,
    RtcConnectionState,
    // Synchronized effects
    Effect,
    // Warmup games
    WarmupStarted,
    WarmupStateUpdate,
    WarmupStopped,
    RelayedWarmupStart,
    RelayedWarmupInput,
    RelayedWarmupStop,
    // Host-side game engine
    RelayedAction,
    RelayedLobbyAction,
    RelayedSelectGame,
    RelayedStartGame,
    RelayedPauseGame,
    RelayedResumeGame,
    RelayedReturnToLobby,
    RelayedGameCatalogRequest,
    ActionAck,
    HostLobbyUpdate,
    // Global stats
    GlobalStats,
]);
//# sourceMappingURL=messages.js.map